import {
    concat,
    equals,
    mergeDeepWith,
    pipe,
    uniq,
    objOf,
    mergeAll,
    call,
    useWith,
    omit,
    add,
    identity,
    ifElse,
    always,
    map,
    cond,
    T,
    prop,
    has,
    defaultTo,
    propOr,
    head
} from 'ramda'
import { useEffect, useRef, useState } from 'react'

import { CardCollection } from './CardCollection.js'
import { CardCollectionFilterControlsPanel } from "./CardCollectionFilterControlsPanel.js"
import CardCollectionSearchControl from "./CardCollectionSearchControl.js"
import pluralize from 'pluralize'
import { useFetch } from '../hooks/useFetch.js'
import { useFetchPosts } from '../hooks/index.js'

import { SalsaEventCardImage } from './SalsaEventCardImage.js'

const locationByEventType = cond([
    [equals('Webinars'), always('Virtual')],
    [T, always('')]
])

const log = (v) => {
    console.log(v)
    return v
}
const getFirstCategoryName = pipe(
    propOr([], 'categories'),
    head,
    defaultTo({}),
    propOr('', 'name')
)

export function PostCollection(props = {}) {
    // deconstruct props
    const {
        query = {},
        endpoint,
        searchControlEnabled = false,
        searchControlPlaceholder = 'Enter keywords',
        searchControlLabel = 'Search',
        filterControlsEnabled = false,
        taxonomyFilters = [],
        columns = 3,
        rows = 3,
        sortByEnabled,
        loadMoreEnabled,
        upcomingOnlyEnabled
    } = props

    const queryRef = useRef(query);
    // Handles state for the visible cards in the collection
    const [cards, setCards] = useState([])
    const [page, setPage] = useState(1)

    // Handles state for filter options
    const [filters, setFilters] = useState([])

    // Custom hook to handle state of posts
    // fetched from the wp database
    const { posts, fetchPosts } = useFetchPosts({
        query: upcomingOnlyEnabled ? omit(['startDate', 'endDate'], query) : query,
        endpoint,
        initialValue: []
    })

console.log('test');
console.log('endpoint');
    // Custom hook to handle state of post types
    // fetched from the server
    const [postTypes, fetchPostTypes] = useFetch(
        `${endpoint}wp/v2/types?per_page=100&_embed`,
        {},
        {}
    )

    // Manages state for taxonomies fetch from the wp database
    const [taxonomies, fetchTaxonomies] = useFetch(
        `${endpoint}wp/v2/taxonomies?per_page=100&_embed`, {}, {}
    )

    // Manages state for terms
    const [terms, setTerms] = useState([])

    // Updated `cards` whenever `posts` or `post types` change.
    useEffect(() => {
        const cards = posts && posts.length > 0
            ? posts.map((post = {}) => ({
                title: post.title,
                id: post.id,
                href: post.link,
                image: SalsaEventCardImage(post),
                badge: {
                    label: getPostTypeNameByPost(postTypes, post)
                },
                body: post?.excerpt || '',
                eventDetails: {
                    category: getFirstCategoryName(post),
                    date: prop('start_date', post),
                    location: pipe(
                        getFirstCategoryName,
                        locationByEventType
                    )(post)
                }
            })) : []

        setCards(cards)
    }, [posts, postTypes])

    // React hook to update terms whenever posts updates
    useEffect(() => {
        const terms = {}
        for (const post of posts) {
            if (!post || !post._embedded || !post._embedded['wp:term']) continue
            for (const termsList of post._embedded['wp:term']) {
                for (const term of termsList) {
                    terms[term.id] = term
                }
            }
        }
        setTerms(Object.values(terms))
    }, [posts])

    // Update filters whenever terms or taxonomies changes
    useEffect(() => {
        if (!filterControlsEnabled) return
        if (!terms) return
        if (!taxonomies) return
        if (!taxonomyFilters) return
        setFilters(filters => {
            const newFilters = [...filters]
            for (const slug in taxonomies) {
                const options = terms
                    .filter(term => term.taxonomy === slug)
                    .map(term => ({
                        id: term.id,
                        label: term.name,
                        value: term.slug,
                        type: 'checkbox',
                    }))
                if (options.length < 1) continue;

                const existingFilter = newFilters.find(filter => (
                    filter.name === taxonomies[slug].rest_base
                ))

                if (existingFilter) {
                    existingFilter.options = options.map(opt => {
                        const oldOption = existingFilter.options.find(oldOpt => oldOpt.id === opt.id) || {}
                        return { ...oldOption, ...opt }
                    })
                    continue;
                }

                newFilters.push({
                    id: slug,
                    label: `by ${taxonomies[slug].name}`,
                    name: taxonomies[slug].rest_base,
                    options
                })


            }

            return newFilters
                .filter(f => taxonomyFilters[f.id])
                .sort((a, b) => a.name > b.name ? 1 : -1)
        })
    }, [terms, taxonomies, taxonomyFilters, filterControlsEnabled])

    useEffect(() => {

        // fetch posts requires a query argument
        // which should be an object resulting from
        // a deep merge of the initial query prop
        // selected filters
        const filterQuery = filters.reduce((obj, filter) => {
            const activeTermIds = [...new Set(filter.options
                .filter(opt => opt.isActive)
                .map(opt => opt.id))]
            return activeTermIds.length > 0 ? {
                ...obj,
                [filter.name]: activeTermIds
            } : obj
        }, {})

        const newQuery = pipe(
            mergeDeepWith(pipe(concat, uniq), query),
            ifElse(
                always(upcomingOnlyEnabled),
                omit(['startDate', 'endDate']),
                identity
            )
        )(filterQuery)
        const isSameQuery = equals(queryRef.current, newQuery)
        if (!isSameQuery) {
            queryRef.current = {
                ...newQuery
            }
            fetchPosts(queryRef.current)
        }
    }, [filters, query, upcomingOnlyEnabled])

    return (
        <div className="max-w-screen-xl mx-auto grid gap-4">
            <div className="gap-4 grid lg:grid-flow-cols w-full grid-cols-1fr-maxcontent">
                {
                    /**
                     * TODO: Implement keyword search (title and excerpt)
                     * */
                }
                <CardCollectionSearchControl
                    isEnabled={searchControlEnabled}
                    placeholder={searchControlPlaceholder}
                    label={searchControlLabel}
                />
                <CardCollectionFilterControlsPanel
                    isEnabled={filterControlsEnabled}
                    filters={filters}
                    onChange={(changed) => setFilters(changed.filters)}
                />
            </div>



            <div className="grid gap-4">
                {/* <span className="text-xs text-black">Displaying {cards.length} of {posts.length} results</span> */}
                <CardCollection cards={cards} columns={columns} rows={rows} page={page} />
            </div>
            {
                loadMoreEnabled && cards.length > columns * rows * page
                    ? (
                        <div className="text-center">
                            <button
                                onClick={() => {
                                    setPage(add(1))
                                }}
                                className="bg-blue rounded text-white px-4 py-2 font-semibold text-base cursor-pointer hover:bg-blue-darker">Load More</button>
                        </div>
                    )
                    : null
            }
        </div>

    )
}

export default PostCollection

function getPostTypeNameByPost(postTypes, post) {
    return pluralize.singular(postTypes[post.type]?.name || '')
}
